import type {
  C2CQuestionsResponse,
  CompanyProfile,
  CompanyToSwitch,
  CompanyWithExtend,
  ProfileHistoryResponse,
} from '@/api/companies/types';
import type {
  C2CQuestion,
  C2CQuestionListParams,
  C2CQuestionPayload,
  DataPointSearchResult,
  PatchTemplatePayload,
  ProfileTemplate,
  TemplateId,
} from '@/api/network/types';
import { API_ROOT_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

export async function fetchCompaniesToSwitch(): Promise<CompanyToSwitch[]> {
  try {
    return await apiFetch<CompanyToSwitch[]>(
      '/v3/user-companies/companies-to-switch',
    );
  } catch {
    return [];
  }
}

export async function switchCompany(companyId: string): Promise<void> {
  const raw = localStorage.getItem('retraced:context');
  if (!raw) return;
  const parsed = JSON.parse(raw);
  parsed.state.companyId = companyId;
  localStorage.setItem('retraced:context', JSON.stringify(parsed));
}

// ─── Company profile & history ────────────────────────────────────────────────

export async function fetchCompanyProfile(
  companyId: string,
): Promise<CompanyProfile> {
  // Lives on the main API gateway (`/api/companies/templates/:templateId/profile/:forCompanyId`),
  // NOT on the companies-service microservice — mirrors `patchProfileTemplate`.
  const res = await apiFetch<CompanyProfile>(
    `/api/companies/templates/about/profile/${companyId}`,
    {},
    API_ROOT_URL,
  );
  if (!res) throw new Error('Company not found');
  return res;
}

export async function fetchProfileHistory(
  companyId: string,
  offset: number,
): Promise<ProfileHistoryResponse> {
  return apiFetch<ProfileHistoryResponse>(
    `/companies-service/v1/audit/profiles/${companyId}?offset=${offset}`,
    {},
    API_ROOT_URL,
  );
}

// Lives on the main API gateway (`/api/companies/:forCompanyId/data-points-answers`),
// NOT on the companies-service microservice — mirrors `fetchCompanyProfile`.
export async function searchDataPointsByName(
  companyId: string,
  qs: string,
): Promise<{ data: DataPointSearchResult[] }> {
  return apiFetch<{ data: DataPointSearchResult[] }>(
    `/api/companies/${companyId}/data-points-answers?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchProfileTemplate(
  templateId: TemplateId,
  companyId: string,
): Promise<ProfileTemplate> {
  // Lives on the main API gateway (`/api/companies/templates/:templateId/profile/:forCompanyId`),
  // NOT on the companies-service microservice — mirrors `patchProfileTemplate`.
  const res = await apiFetch<ProfileTemplate>(
    `/api/companies/templates/${templateId.toLowerCase()}/profile/${companyId}`,
    {},
    API_ROOT_URL,
  );
  if (!res) throw new Error('Profile template not found');
  return res;
}

export interface AnswerC2CQuestionItem {
  questionId: string;
  answer?: Record<string, unknown>;
  options?: Array<Record<string, unknown>>;
}

export async function answerC2CQuestions(
  companyId: string,
  payload: AnswerC2CQuestionItem[],
): Promise<void> {
  await apiFetch<void>(
    `/v3/companies/${companyId}/c2c-custom-questions/answer`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function fetchC2CQuestions(
  companyId: string,
  params: { page: number; count: number },
): Promise<C2CQuestionsResponse> {
  const qs = new URLSearchParams({
    page: String(params.page),
    count: String(params.count),
  });
  return apiFetch<C2CQuestionsResponse>(
    `/v3/companies/${companyId}/c2c-custom-questions?${qs.toString()}`,
  );
}

// ─── C2C Builder (own-company field management) ──────────────────────────────

export async function fetchC2CQuestionsList(
  params: C2CQuestionListParams,
): Promise<C2CQuestionsResponse> {
  const qs = new URLSearchParams();
  if (params.page != null) qs.set('page', String(params.page));
  if (params.count != null) qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return apiFetch<C2CQuestionsResponse>(
    `/v3/companies/c2c-custom-questions/list?${qs.toString()}`,
  );
}

export async function fetchC2CQuestionById(id: string): Promise<C2CQuestion> {
  return apiFetch<C2CQuestion>(`/v3/companies/c2c-custom-questions/${id}`);
}

export async function createC2CQuestion(
  payload: C2CQuestionPayload[],
): Promise<void> {
  await apiFetch<void>('/v3/companies/c2c-custom-questions', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateC2CQuestion(
  id: string,
  payload: Pick<
    C2CQuestionPayload,
    'title' | 'hintText' | 'apiFieldIdentifier'
  >,
): Promise<void> {
  await apiFetch<void>(`/v3/companies/c2c-custom-questions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function deleteC2CQuestions(ids: string[]): Promise<void> {
  await apiFetch<void>('/v3/companies/c2c-custom-questions/bulk-remove', {
    method: 'PUT',
    body: JSON.stringify(ids),
  });
}

export async function patchProfileTemplate(
  templateId: TemplateId,
  payload: PatchTemplatePayload,
): Promise<void> {
  await apiFetch<void>(
    `/api/companies/templates/${templateId.toLowerCase()}/answer`,
    { method: 'PATCH', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
}

export async function fetchCompanyWithExtend(
  companyId: string,
): Promise<CompanyWithExtend> {
  return apiFetch<CompanyWithExtend>(`/v3/companies/${companyId}`);
}
